import { useCallback } from 'react';
import { useAppEnvironmentProvider } from 'contexts/AppEnvironmentContext';
import { ELECTRON_MEET_WINDOW_EVENTS } from '@web-chat-core/toolkit/multi-window/constants';
import { selectEmbeddedWindowData } from '../../../state/selectors/app';

export const useEmbeddedWindow = () => {
    const { multiWindowMessagingService } = useAppEnvironmentProvider();
    const windowData = selectEmbeddedWindowData();
    const { descriptor } = windowData || {};

    const callAccepted = useCallback(() => {
        if (!descriptor) {
            return;
        }
        multiWindowMessagingService?.emit(ELECTRON_MEET_WINDOW_EVENTS.CALL_ACCEPTED, null, descriptor);
    }, [descriptor, multiWindowMessagingService]);

    const callRequireAuthData = useCallback(() => {
        if (!descriptor) {
            return;
        }
        multiWindowMessagingService?.emit(ELECTRON_MEET_WINDOW_EVENTS.PREVIEW_COMPLETE, null, descriptor);
    }, [descriptor, multiWindowMessagingService]);

    const callRejected = useCallback(() => {
        if (!descriptor) {
            return;
        }
        multiWindowMessagingService?.emit(ELECTRON_MEET_WINDOW_EVENTS.CALL_REJECTED, null, descriptor);
    }, [descriptor, multiWindowMessagingService]);

    const callCompleted = useCallback(() => {
        if (!descriptor) {
            return;
        }
        multiWindowMessagingService?.emit(ELECTRON_MEET_WINDOW_EVENTS.CALL_COMPLETED, null, descriptor);
    }, [descriptor, multiWindowMessagingService]);

    const setDontShowPreviewAgain = useCallback(
        (isSkipPreviewWindow: boolean = false) => {
            if (!descriptor) {
                return;
            }
            multiWindowMessagingService?.emit(
                ELECTRON_MEET_WINDOW_EVENTS.SHOW_PREVIEW_SET,
                { isSkipPreviewWindow },
                descriptor,
            );
        },
        [descriptor, multiWindowMessagingService],
    );

    const setMicAlwaysOnByDefault = useCallback(
        (isMicAlwaysOnByDefault: boolean = false) => {
            if (!descriptor) {
                return;
            }
            multiWindowMessagingService?.emit(
                ELECTRON_MEET_WINDOW_EVENTS.MIC_ALWAYS_ON_BY_DEFAULT,
                { isMicAlwaysOnByDefault },
                descriptor,
            );
        },
        [descriptor, multiWindowMessagingService],
    );

    const setCameraAlwaysOnByDefault = useCallback(
        (isCameraAlwaysOnByDefault: boolean = false) => {
            if (!descriptor) {
                return;
            }
            multiWindowMessagingService?.emit(
                ELECTRON_MEET_WINDOW_EVENTS.CAMERA_ALWAYS_ON_BY_DEFAULT,
                { isCameraAlwaysOnByDefault },
                descriptor,
            );
        },
        [descriptor, multiWindowMessagingService],
    );

    return {
        callAccepted,
        callRequireAuthData,
        callRejected,
        callCompleted,
        setDontShowPreviewAgain,
        setMicAlwaysOnByDefault,
        setCameraAlwaysOnByDefault,
    };
};
